import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, AlertTriangle, Clock, CheckCircle } from "lucide-react";

interface PatientStatsProps {
  clinicId: string;
}

export function PatientStats({ clinicId }: PatientStatsProps) {

  const { data: stats, isLoading } = useQuery({
    queryKey: ['patient-stats', clinicId],
    queryFn: async () => {
      try {
        console.log("Fetching patient stats with clinicId:", clinicId);
        
        // Get total patients count using the same RPC function as PatientList
        const { data: patientsData, error } = await supabase
          .rpc('get_patients_by_clinic', { search_query: '' });
        
        const totalPatients = patientsData?.length || 0;
        
        console.log("Patient count query result:", { totalPatients, error });
        console.log("Patient data sample:", patientsData?.slice(0, 5));
        
        // Get recent triage submissions with urgency levels
        const { data: triageData, error: triageError } = await supabase
          .from('triage_submissions')
          .select('urgency, patient_id')
          .eq('clinic_id', clinicId)
          .order('created_at', { ascending: false });
        
        console.log("Triage data:", { count: triageData?.length, error: triageError });

        // Count urgency levels
        const urgencyCounts = triageData?.reduce((acc, triage) => {
          acc[triage.urgency] = (acc[triage.urgency] || 0) + 1;
          return acc;
        }, {} as Record<string, number>) || {};
        
        // Debug info removed

        return {
          totalPatients: totalPatients || 0,
          immediate: urgencyCounts.immediate || 0,
          urgent: urgencyCounts.urgent || 0,
          standard: urgencyCounts.standard || 0,
          nonUrgent: urgencyCounts['non-urgent'] || 0
        };
      } catch (error) {
        console.error("Error in patient stats query:", error);
        return {
          totalPatients: 0,
          immediate: 0,
          urgent: 0,
          standard: 0,
          nonUrgent: 0
        };
      }
    }
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="w-24 h-4 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="w-16 h-8 bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Patients",
      value: stats?.totalPatients,
      icon: Users,
      color: "bg-blue-500"
    },
    {
      title: "Immediate Care",
      value: stats?.immediate,
      icon: AlertTriangle,
      color: "bg-red-500"
    },
    {
      title: "Urgent Cases",
      value: stats?.urgent,
      icon: Clock,
      color: "bg-orange-500"
    },
    {
      title: "Standard/Non-Urgent",
      value: (stats?.standard || 0) + (stats?.nonUrgent || 0),
      icon: CheckCircle,
      color: "bg-green-500"
    }
  ];

  return (
    <div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {stat.title}
              </CardTitle>
              <div className={`p-2 rounded-full ${stat.color}`}>
                <stat.icon className="h-4 w-4 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value || 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      

    </div>
  );
}
